import {
  QUOTA_EXCEEDED_ALERT,
  QUOTA_EXCEEDED_ERROR,
  UNEXPECTED_ERROR,
} from "../constants/ErrorText";
import { UAParser } from "my-ua-parser";

// * Method removes specific properties from the login response
export const filterLoginResponse = (loginResponse) => {
  const chainStr = [
    "dealers",
    "operators",
    "permits",
    "species",
    "chartarea",
    "gear",
    "port",
    "uom",
  ];

  let filteredResponse = { ...loginResponse };
  Object.entries(filteredResponse).forEach(([key]) => {
    if (chainStr.some((el) => key.toLowerCase().startsWith(el))) {
      delete filteredResponse[key];
    }
  });

  return filteredResponse;
};

export const setStorageValue = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error(error);
    const errorMessage =
      error.name === QUOTA_EXCEEDED_ERROR || error.code === 22
        ? QUOTA_EXCEEDED_ALERT
        : UNEXPECTED_ERROR;
    console.error(errorMessage);
    alert(errorMessage);
    throw Error(errorMessage);
  }
};

export const getStorageValue = (key) => {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : undefined;
  } catch (error) {
    console.error(error);
    throw Error(error);
  }
};

export const removeStorageValue = (key) => {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.log(error);
    throw Error(error);
  }
};

export async function storageCheck() {
  if ("storage" in navigator && "estimate" in navigator.storage) {
    // * Both values returned here are in bytes, therefore are converted to MB
    const { usage, quota } = await navigator.storage.estimate();
    const percentUsed = Math.round((usage / quota) * 100);
    const usageInMib = Math.round(usage / (1024 * 1024));
    const quotaInMib = Math.round(quota / (1024 * 1024));
    const spaceAvailable = usageInMib < quotaInMib;
    const availableSpace = quota > usage ? quota - usage : 0;
    console.info("[storageCheck] Available Space", availableSpace);

    const details = `${usageInMib} out of ${quotaInMib} MiB used (${percentUsed}%)`;

    return { usageInMib, quotaInMib, details, spaceAvailable };
  }
  // * In case browser's version does not support Storage API functionality
  return { usageInMib: "N/A", quotaInMib: "N/A", details: "N/A", spaceAvailable: "N/A" };
}

export function getSizeOfObject(object) {
  const jsonString = JSON.stringify(object);
  const blob = new Blob([jsonString]).size;
  const blobInMib = Math.round(blob / (1024 * 1024));
  return blobInMib;
}

/**
 * Gets browser and OS info, prioritizing the modern User-Agent Client Hints API
 * with a fallback to user-agent string parsing.
 *
 */
export function getBrowsersInfo() {
  try {
    // 1. First, try the modern User-Agent Client Hints API
    if (navigator.userAgentData) {
      const platform = navigator.userAgentData.platform || "n/a";

      // The 'brands' array is ordered from least to most specific.
      // We reverse it to find the most significant brand name first.
      const significantBrand = [...navigator.userAgentData.brands]
        .reverse()
        .find((brand) => !brand.brand.includes("Not")); // Filter out "Not/A)Brand"

      const browser = significantBrand
        ? `${significantBrand.brand} ${significantBrand.version}`
        : "n/a";
      return { browser, os: platform };
    }

    // 2. Fallback to the older userAgent string parsing method
    const ua = UAParser(navigator.userAgent);
    return {
      // Using optional chaining (?) for extra safety
      browser: `${ua.browser?.name || ""} ${ua.browser?.version || ""}`.trim(),
      os: ua.os?.name || "n/a",
    };
  } catch (error) {
    // 3. Final catch-all in case of any errors
    console.error("Error getting browser info:", error);
    return { browser: "n/a", os: "n/a" };
  }
}
